import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CalendarCheck, Calendar, Trophy, LogOut, MapPin } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth';
import { useVenue } from '@/lib/venue';
import { cn } from '@/lib/utils';

const links = [
  { to: '/app/bookings', label: 'My Bookings', icon: CalendarCheck },
  { to: '/app/book', label: 'Book a Court', icon: Calendar },
  { to: '/app/memberships', label: 'Memberships', icon: Trophy },
];

export default function CustomerLayout() {
  const { user, logout } = useAuth();
  // Only worth showing once there's an actual choice — stays invisible
  // while there's only one venue in the system.
  const { venues, selectedVenue, clearVenue } = useVenue();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    // The venue picker persists across in-app navigation now (see
    // lib/venue.jsx) — but a logout should still start the next login
    // fresh, since VenueProvider itself never remounts on auth change.
    clearVenue();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-obsidian text-white flex flex-col">
      <header className="sticky top-0 z-40 bg-obsidian/90 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            {venues.length > 0 && (
              <Link
                to="/app/venues"
                state={{ redirectTo: location.pathname }}
                className="hidden sm:flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-volt"
                data-testid="change-venue-link"
              >
                <MapPin size={12} />
                {selectedVenue?.name || 'Choose venue'}
              </Link>
            )}
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-medium text-white" data-testid="navbar-user-name">{<img
  src={user?.picture || '/default-avatar.png'}
  alt="profile"
  className="w-8 h-8 rounded-full"
/>}</span>
              <span className="text-sm font-medium text-white" data-testid="navbar-user-name">{user?.name }</span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-volt">Member</span>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button">
              <LogOut size={14} /> Logout
            </Button>
          </div> 
        </div>
        <nav className="container mx-auto px-4 flex gap-1 overflow-x-auto">
          {links.map((l) => {
            const active = location.pathname.startsWith(l.to);
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`customer-nav-${l.label.replace(/\s/g, '-').toLowerCase()}`}
                className={cn(
                  'inline-flex items-center gap-2 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.2em] border-b-2 transition-colors whitespace-nowrap',
                  active ? 'border-volt text-volt' : 'border-transparent text-white/50 hover:text-white',
                )}
              >
                <l.icon size={14} /> {l.label}
              </Link>
            );
          })}
        </nav>
      </header>
      <main className="flex-1 container mx-auto px-4 py-8 sm:py-12 max-w-6xl">
        <Outlet />
      </main>
    </div>
  );
}
